/**
 * Step-level validation logic
 */

import {
    validateName,
    validateEmail,
    validateBirthday,
    validateSex,
    validateCity,
    validateHomeCity,
    validatePassword,
    validateUsername
} from './validators.js';
import { getFieldForStep } from '../steps.js';
import { daysInMonth, getYearOptions } from '../utils/dateHelpers.js';

export function validateStep(step, state) {
    switch (step) {
        case 1:
            return validateEmail(state.email);
        case 2:
            return validateName(state.name);
        case 3:
            return validateBirthday(state.day, state.month, state.year, state.minAge, Boolean(state.birthdayDroppedDay));
        case 4:
            return validateSex(state.sex);
        case 5:
            return validateCity(state.cityId);
        case 6:
            return validateHomeCity(state.homeCityId);
        case 7:
            return validatePassword(state.password, state.passwordMinLength, state.passwordMaxLength, state.passwordMaxBytes);
        case 8:
            return validateUsername(state.username);
        default:
            return { valid: true };
    }
}

export function isStepValid(step, state) {
    return validateStep(step, state).valid;
}

export function getStepError(step, state) {
    const field = getFieldForStep(step);

    // Don't show errors during initialization
    if (state.isInitializing) {
        return null;
    }

    // Don't show errors until field is touched
    if (!state.touched[field]) {
        return null;
    }

    // A taken email is not an error. The step offers sign-in instead (stepHasAccount).
    if (isHasAccountError(state.errors[field])) {
        return null;
    }

    // Check server errors FIRST (they are authoritative)
    const serverError = serverErrorMessage(state.errors[field]);
    if (serverError) {
        return serverError;
    }

    const result = validateStep(step, state);

    return result.valid ? null : result.error;
}

// A /v1/account refusal carries its text as message, msg or a bare string.
export function serverErrorMessage(error) {
    if (typeof error === 'string') {
        return error || null;
    }

    return error?.message || error?.msg || null;
}

// Server codes whose refusal gives a sign-in link. The value is ValidationException::CODE_EMAIL_PROVIDER_BANNED.
// The step_error macro shows its providerRefusal text in place of the error message for these codes.
const SIGN_IN_ERROR_CODES = ['email_provider_banned'];

// The value is ValidationException::CODE_EMAIL_IN_USE.
const HAS_ACCOUNT_CODE = 'email_in_use';

function isHasAccountError(error) {
    return error?.code === HAS_ACCOUNT_CODE;
}

/**
 * The step's email already has an account, so the step offers sign-in and a
 * password reset in place of Next.
 */
export function stepHasAccount(step, state) {
    const field = getFieldForStep(step);

    return !state.isInitializing && Boolean(state.touched[field]) && isHasAccountError(state.errors[field]);
}

export function stepErrorOffersSignIn(step, state) {
    if (getStepError(step, state) === null) {
        return false;
    }

    const error = state.errors[getFieldForStep(step)];

    return SIGN_IN_ERROR_CODES.includes(error?.code);
}

// The value is EmailProviderAllowedRule::TYPO_FAILURE.
const TYPO_CODE = 'email_domain_typo';

function domainOf(address) {
    return String(address || '').trim().toLowerCase().split('@')[1] || '';
}

// The typo hint names the correction the user is typing, so it stays while a different domain is typed.
function keepsTypoHint(state, field, error) {
    const suggested = domainOf(error.meta?.suggestion);
    const typed = domainOf(state[field]);

    return suggested !== '' && typed !== '' && typed !== suggested;
}

// A server error answers the value that was submitted, so it no longer applies once the field changes.
export function clearServerError(state, field) {
    const error = state.errors[field];
    if (error === undefined || (error?.code === TYPO_CODE && keepsTypoHint(state, field, error))) {
        return;
    }

    const rest = { ...state.errors };
    delete rest[field];
    state.errors = rest;
}

/**
 * Keeps the birthday selects on dates that exist. A month or a year that no
 * longer has the picked day clears the day, keeps it in birthdayDroppedDay
 * and shows the invalid-day error at once. A later month or year that has the
 * dropped day puts it back, so arrow keys through a short month keep the day.
 * A second call on the same state changes nothing.
 *
 * announce: false clears a restored impossible day or year without an error.
 */
export function reconcileBirthday(state, announce = true) {
    if (state.year && !getYearOptions(state.minAge).includes(String(state.year))) {
        state.year = '';
    }

    const days = daysInMonth(state.month, state.year);

    if (state.day && Number(state.day) > days) {
        if (announce) {
            state.birthdayDroppedDay = state.day;
            state.touched.birthday = true;
        }
        state.day = '';
        return;
    }

    if (!state.day && state.birthdayDroppedDay && Number(state.birthdayDroppedDay) <= days) {
        state.day = state.birthdayDroppedDay;
        state.birthdayDroppedDay = '';
    }
}

// A picked day ends the invalid-day error. Its own validation takes over.
export function pickBirthdayDay(state) {
    if (state.day) {
        state.birthdayDroppedDay = '';
    }
}

export function getFirstInvalidStep(state, totalSteps) {
    for (let step = 1; step <= totalSteps; step++) {
        if (!isStepValid(step, state)) {
            return step;
        }
    }
    return null;
}

export function markStepTouched(step, state) {
    const field = getFieldForStep(step);
    state.touched[field] = true;
}
